import React, { useEffect, useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Sparkle, MapPin, SignOut, Ticket, Gift, CreditCard, Gear, Question, Receipt, CaretDown, NavigationArrow, Heart } from "@phosphor-icons/react";
import { Logo } from "./shared";
import { PlayStoreLink } from "./PlayStoreLink";
import { Button } from "./ui/button";
import { useConfig, getSavedCity, saveCity } from "../lib/useConfig";
import { useAuth } from "../context/AuthContext";
import { detectLocationCity } from "../lib/geo";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import AIChatbot from "./AIChatbot";

const MENU = [
  { to: "/bookings", label: "My Bookings", icon: Receipt },
  { to: "/saved", label: "Saved Events", icon: Heart },
  { to: "/rewards", label: "Event Credits", icon: Gift },
  { to: "/payment-methods", label: "Payment Methods", icon: CreditCard },
  { to: "/settings", label: "Account Settings", icon: Gear },
  { to: "/support", label: "Help & Support", icon: Question },
];

export default function CustomerLayout() {
  const { cities } = useConfig();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [city, setCity] = useState(getSavedCity());
  const [headerGenres, setHeaderGenres] = useState([]);
  const [detecting, setDetecting] = useState(false);

  useEffect(() => {
    api.get("/genres/header").then((r) => setHeaderGenres(r.data || [])).catch(() => {});
  }, []);

  const applyCity = (c) => {
    setCity(c); saveCity(c);
    if (location.pathname === "/discover") navigate(0);
  };

  const onCity = (c) => {
    if (c === "__gps__") {
      setDetecting(true);
      detectLocationCity(cities)
        .then(({ detected, matched }) => {
          if (matched) { toast.success(`Showing events near ${matched}.`); applyCity(matched); }
          else toast.error(`We don't cover ${detected || "your area"} yet. Please pick a city.`);
        })
        .catch(() => toast.error("Couldn't access your location. Please allow location access or pick a city."))
        .finally(() => setDetecting(false));
      return;
    }
    applyCity(c);
  };

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="mx-auto max-w-7xl px-4 md:px-6 h-16 flex items-center gap-3">
          <Link to="/discover" data-testid="nav-logo"><Logo /></Link>

          <div className="hidden sm:flex items-center ml-2">
            <Select value={city} onValueChange={onCity}>
              <SelectTrigger data-testid="city-selector" className="w-[150px] h-9 border-border bg-card">
                <MapPin size={16} className="text-primary shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__gps__" data-testid="use-my-location">
                  <span className="inline-flex items-center gap-2"><NavigationArrow size={14} /> {detecting ? "Detecting…" : "Use my location"}</span>
                </SelectItem>
                {cities.map((c) => <SelectItem key={c.id} value={c.name} data-testid={`city-opt-${c.name}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <nav className="hidden lg:flex items-center gap-1 ml-3" data-testid="header-genres">
            {headerGenres.map((g) => (
              <Link key={g.id} to={`/discover?genre=${encodeURIComponent(g.name)}`} data-testid={`header-genre-${g.name}`}
                className="px-3 h-9 inline-flex items-center rounded-full text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                {g.name}
              </Link>
            ))}
          </nav>

          <div className="flex-1" />

          <Button asChild variant="outline" className="h-9 rounded-full border-foreground/20 hidden sm:inline-flex" data-testid="ask-ai-nav">
            <Link to="/ask-ai"><Sparkle weight="fill" className="text-primary mr-1.5" size={16} /> Ask AI</Link>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-border bg-card pl-1 pr-2 h-9 btn-press">
                <span className="grid place-items-center w-7 h-7 rounded-full bg-foreground text-background text-xs font-bold">{initials}</span>
                <CaretDown size={14} className="text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.name || user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild><Link to="/ask-ai" className="sm:hidden"><Sparkle size={16} className="mr-2 text-primary" /> Ask AI</Link></DropdownMenuItem>
              {MENU.map((m) => (
                <DropdownMenuItem key={m.to} asChild>
                  <Link to={m.to} data-testid={`menu-${m.label.replace(/\s/g, "-").toLowerCase()}`}>
                    <m.icon size={16} className="mr-2" /> {m.label}
                  </Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn" className="text-destructive">
                <SignOut size={16} className="mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="flex-1">
        <Outlet context={{ city }} />
      </main>

      <AIChatbot />

      <footer className="border-t border-border mt-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 flex flex-col md:flex-row justify-between gap-6">
          <div>
            <Logo />
            <p className="text-sm text-muted-foreground mt-2 max-w-xs">AI-first event discovery, ticketing & organizer platform.</p>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <Link to="/legal/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy</Link>
            <Link to="/legal/customer-terms" className="text-muted-foreground hover:text-foreground">Terms</Link>
            <Link to="/legal/refund-policy" className="text-muted-foreground hover:text-foreground">Refunds</Link>
            <Link to="/support" className="text-muted-foreground hover:text-foreground">Support</Link>
          </div>
          <PlayStoreLink testId="customer-play-store-footer" footer />
        </div>
      </footer>
    </div>
  );
}
